import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Eye, TrendingUp } from "lucide-react";
import { API } from "@/App";
import { WHATSAPP_SALES_URL } from "@/lib/sales";

const OVERLAY_THEMES = {
  green:  "from-emerald-900/85 via-emerald-900/55 to-transparent",
  amber:  "from-amber-900/85 via-amber-900/55 to-transparent",
  dark:   "from-black/85 via-black/55 to-black/20",
  blue:   "from-blue-900/85 via-blue-900/55 to-transparent",
  red:    "from-red-900/85 via-red-900/55 to-transparent",
  purple: "from-purple-900/85 via-purple-900/55 to-transparent",
};
const BADGE_BG = {
  green: "bg-emerald-500/90", amber: "bg-amber-500/90", dark: "bg-amber-500/90",
  blue: "bg-blue-500/90", red: "bg-rose-500/90", purple: "bg-purple-500/90",
};

const AdminHeroBanner = () => {
  const [b, setB] = useState(null);
  useEffect(() => {
    axios.get(`${API}/hero-banner`).then(r => setB(r.data)).catch(() => setB(null));
  }, []);
  if (!b || !b.enabled || !b.title) return null;
  const overlay = OVERLAY_THEMES[b.background] || OVERLAY_THEMES.dark;
  const badgeBg = BADGE_BG[b.background] || BADGE_BG.dark;
  const go = (link) => {
    // All buying-intent CTAs funnel to WhatsApp; only external http links are honoured as-is.
    const target = link && (link.startsWith("http://") || link.startsWith("https://")) ? link : WHATSAPP_SALES_URL;
    window.open(target, "_blank", "noopener,noreferrer");
  };
  return (
    <section className="pt-20 relative" data-testid="admin-hero-banner">
      <div className="relative h-[520px] sm:h-[560px] w-full overflow-hidden">
        {b.image_url ? (
          <img src={b.image_url} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900" />
        )}
        <div className={`absolute inset-0 bg-gradient-to-r ${overlay}`} />
        <div className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-12 lg:px-20 max-w-5xl">
          {b.badge && (
            <Badge className={`mb-4 w-fit ${badgeBg} text-white border-0 text-sm px-4 py-1`} data-testid="hero-badge">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5 inline" /> {b.badge}
            </Badge>
          )}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight"
            style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}
            data-testid="hero-title"
          >
            {b.title}
          </h1>
          {b.subtitle && (
            <p
              className="text-lg text-gray-100 max-w-xl mb-8 leading-relaxed whitespace-pre-line"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
              data-testid="hero-subtitle"
            >
              {b.subtitle}
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {b.button_text && b.button_link && (
              <Button
                size="lg"
                onClick={() => go(b.button_link)}
                className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold rounded-full px-8 h-12 text-base shadow-lg"
                data-testid="hero-cta-btn"
              >
                {b.button_text} <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
            {b.button2_text && b.button2_link && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => go(b.button2_link)}
                className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 h-12 text-base backdrop-blur-sm"
                data-testid="hero-cta2-btn"
              >
                <Eye className="h-5 w-5 mr-2" /> {b.button2_text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminHeroBanner;
